"use client";

// Small fetch-state hook shared by the dashboard panels.
// Handles abort-on-unmount, stale-response guarding and a manual reload so
// every panel gets the same loading / error / retry behaviour.

import { useCallback, useEffect, useState } from "react";
import { ApiError } from "./api";

export interface ApiState<T> {
  data: T | null;
  loading: boolean;
  error: string | null;
  reload: () => void;
}

interface Result<T> {
  key: string;
  data: T | null;
  error: string | null;
}

export function useApi<T>(
  fetcher: (signal: AbortSignal) => Promise<T>,
  deps: React.DependencyList,
  fallbackError = "Failed to load data.",
): ApiState<T> {
  const [nonce, setNonce] = useState(0);
  const [result, setResult] = useState<Result<T> | null>(null);

  // Identifies the request the current render wants. Anything older is stale,
  // which also gives us `loading` without setting state inside the effect.
  const key = `${deps.map(String).join("|")}#${nonce}`;
  const fresh = result?.key === key;

  const reload = useCallback(() => setNonce((n) => n + 1), []);

  useEffect(() => {
    const ctrl = new AbortController();

    // `fetcher` is deliberately not a dependency: callers pass inline closures,
    // so `deps` (plus `nonce`) is the single source of truth for refetching.
    fetcher(ctrl.signal)
      .then((data) => {
        if (ctrl.signal.aborted) return;
        setResult({ key, data, error: null });
      })
      .catch((err: unknown) => {
        if (ctrl.signal.aborted) return;
        setResult({
          key,
          data: null,
          error: err instanceof ApiError ? err.message : fallbackError,
        });
      });

    return () => ctrl.abort();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);

  return {
    data: fresh ? result.data : null,
    loading: !fresh,
    error: fresh ? result.error : null,
    reload,
  };
}
